import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Shipping protection toggle + checkout popup for the cart drawer.
 * Markup and server-side state come from `blocks/shipping-protection.liquid`.
 */

const DISMISS_KEY = 'kw-protection-dismissed';

function rootUrl() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * Sets the protection quantity and asks the theme to re-render the cart.
 * @param {string} variantId
 * @param {number} quantity
 */
async function setProtection(variantId, quantity) {
  const sections = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'add' : 'remove',
      context: 'cart',
      lines: [{ id: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates: { [variantId]: quantity },
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || 'Cart update failed');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        sections: data.sections,
        items: data.items,
        itemCount: data.item_count,
        source: 'shipping-protection',
        didError: false,
      },
    });
    return true;
  } catch (error) {
    deferred.reject(error);
    console.error('[shipping-protection]', error);
    return false;
  }
}

function goToCheckout() {
  window.location.href = `${rootUrl()}checkout`;
}

class ShippingProtection extends HTMLElement {
  static observedAttributes = ['data-cleanup', 'data-in-cart'];

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#sync();
    this.#cleanup();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  attributeChangedCallback() {
    this.#sync();
    this.#cleanup();
  }

  /** Keeps the switch in line with what is really in the cart. */
  #sync() {
    if (this.#busy) return;
    const input = this.querySelector('.kw-protection__input');
    if (input instanceof HTMLInputElement) input.checked = this.inCart;
  }

  get variantId() {
    return this.dataset.variantId || '';
  }

  get inCart() {
    return this.dataset.inCart === 'true';
  }

  /** Keeps the protection valid: never alone in the cart, never more than one. */
  #cleanup() {
    if (this.#busy || !this.variantId) return;
    const mode = this.dataset.cleanup;
    if (mode === 'remove') this.#run(0);
    else if (mode === 'one') this.#run(1);
  }

  #busy = false;

  /** @param {number} quantity */
  async #run(quantity) {
    this.#busy = true;
    this.classList.add('is-busy');
    const ok = await setProtection(this.variantId, quantity);
    this.classList.remove('is-busy');
    this.#busy = false;
    if (!ok) {
      const input = this.querySelector('.kw-protection__input');
      if (input instanceof HTMLInputElement) input.checked = this.inCart;
    }
    return ok;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('kw-protection__input')) return;
    this.#run(input.checked ? 1 : 0);
  };

  /** Opens the popup. Returns false when there is nothing to show. */
  openPopup() {
    const dialog = this.querySelector('.kw-protection__dialog');
    if (!(dialog instanceof HTMLDialogElement)) return false;

    const yes = dialog.querySelector('[data-protection-yes]');
    const no = dialog.querySelector('[data-protection-no]');

    const onYes = async () => {
      if (yes instanceof HTMLButtonElement) yes.disabled = true;
      await this.#run(1);
      goToCheckout();
    };
    const onNo = () => {
      try {
        sessionStorage.setItem(DISMISS_KEY, '1');
      } catch (e) {
        /* storage unavailable */
      }
      dialog.close();
      goToCheckout();
    };

    yes?.addEventListener('click', onYes, { once: true });
    no?.addEventListener('click', onNo, { once: true });
    dialog.showModal();
    return true;
  }
}

if (!customElements.get('shipping-protection')) {
  customElements.define('shipping-protection', ShippingProtection);

  // Intercept the drawer's checkout button once per page.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target.closest('#checkout, [name="checkout"]') : null;
      if (!target) return;

      const protection = document.querySelector('shipping-protection');
      if (!(protection instanceof ShippingProtection)) return;
      if (protection.dataset.popup !== 'true' || protection.inCart) return;

      let dismissed = false;
      try {
        dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
      } catch (e) {
        dismissed = false;
      }
      if (dismissed) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (!protection.openPopup()) goToCheckout();
    },
    true
  );
}
